import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiCalculator, FiClock, FiActivity, FiMessageSquare,
  FiBook, FiStar, FiChevronUp, FiGrid
} = FiIcons;

const SECTIONS = [
  { id: 'section-calculator', label: 'Calculator', icon: FiCalculator, color: 'text-blue-600', bg: 'bg-blue-50' },
  { id: 'section-activity', label: 'Activity', icon: FiActivity, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  { id: 'section-history', label: 'History', icon: FiClock, color: 'text-slate-600', bg: 'bg-slate-50' },
  { id: 'section-feedback', label: 'Feedback', icon: FiMessageSquare, color: 'text-yellow-600', bg: 'bg-yellow-50' },
  { id: 'section-tips', label: 'Tips', icon: FiStar, color: 'text-orange-600', bg: 'bg-orange-50' },
  { id: 'section-guide', label: 'Guide', icon: FiBook, color: 'text-teal-600', bg: 'bg-teal-50' },
];

export default function SectionNav() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { threshold: 0.3 }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setOpen(false);
  };

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setOpen(false);
  };

  return (
    <div className="fixed bottom-6 left-6 z-40 flex flex-col items-start gap-2">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden w-44"
          >
            {/* Top — Back to Top */}
            <button
              onClick={scrollTop}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-100"
            >
              <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                <SafeIcon icon={FiChevronUp} className="text-slate-600 text-sm" />
              </div>
              <span className="text-xs font-black text-slate-700">Back to Top</span>
            </button>

            {/* Section Links */}
            {SECTIONS.map(({ id, label, icon, color, bg }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors ${active === id ? 'bg-slate-50' : ''}`}
              >
                <div className={`w-7 h-7 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
                  <SafeIcon icon={icon} className={`${color} text-sm`} />
                </div>
                <span className={`text-xs font-black ${active === id ? 'text-blue-600' : 'text-slate-700'}`}>
                  {label}
                </span>
                {active === id && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle Button */}
      <motion.button
        whileTap={{ scale: 0.92 }}
        onClick={() => setOpen((o) => !o)}
        className={`w-12 h-12 rounded-2xl shadow-lg flex items-center justify-center transition-all ${
          open
            ? 'bg-slate-800 text-white'
            : 'bg-white border border-slate-200 text-slate-600'
        }`}
      >
        <SafeIcon icon={FiGrid} className="text-lg" />
      </motion.button>
    </div>
  );
}